import { useCallback, useEffect, useRef, useSyncExternalStore } from 'react'
import { useConnectionRetry } from '../components/status/ConnectionContext'

/**
 * Gemeinsames Polling für die Kartendaten (Live-Drohnen, Abdeckung, Fotos, ...):
 *  - nur bei sichtbarem Tab (Hintergrund-Tabs pausieren), sofort beim Start und bei „Erneut versuchen“ (connection:retry)
 *  - Fehler werden nicht mehr verschluckt: pro Quelle gemerkt und über `useDatenFehler()` sichtbar gemacht.
 *    (Verbindungsfehler meldet zusätzlich der api-client-Interceptor an die Statusleiste – hier geht es um die Anzeige an der Seite.)
 */
const fehlerSet = new Set<string>()
let snapshot: string[] = []
const listeners = new Set<() => void>()

function setzeFehler(key: string, an: boolean) {
  if (an === fehlerSet.has(key)) return
  if (an) fehlerSet.add(key); else fehlerSet.delete(key)
  snapshot = Array.from(fehlerSet).sort()
  listeners.forEach(l => l())
}

/** Namen der Datenquellen, deren letzte Abfrage fehlgeschlagen ist (leer = alles in Ordnung). */
export function useDatenFehler(): string[] {
  return useSyncExternalStore(
    l => { listeners.add(l); return () => { listeners.delete(l) } },
    () => snapshot,
    () => snapshot,
  )
}

/**
 * @param key   Anzeigename der Quelle (z. B. „Abdeckung“)
 * @param holen lädt und setzt den State; bekommt `lebt()` – nach Abbruch/Einsatzwechsel keine Antwort mehr übernehmen. Muss bei Fehler werfen.
 * @param ms    Intervall
 * @param deps  Neustart bei Änderung (z. B. Einsatz-ID)
 * @returns     sofortiges Neuladen
 */
export function usePollen(key: string, holen: (lebt: () => boolean) => Promise<unknown>, ms: number, deps: unknown[], aktiv = true) {
  const holenRef = useRef(holen)
  holenRef.current = holen
  const lauf = useRef<() => void>(() => {})

  useEffect(() => {
    if (!aktiv) { lauf.current = () => {}; return }
    let lebt = true
    const tick = (erzwingen: boolean) => {
      if (!erzwingen && document.visibilityState === 'hidden') return
      holenRef.current(() => lebt)
        .then(() => { if (lebt) setzeFehler(key, false) })
        .catch(() => { if (lebt) setzeFehler(key, true) })
    }
    lauf.current = () => tick(true)
    tick(true)
    const t = setInterval(() => tick(false), ms)
    return () => { lebt = false; clearInterval(t); setzeFehler(key, false) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, ms, aktiv, key])

  useConnectionRetry(() => lauf.current())
  return useCallback(() => lauf.current(), [])
}
